// Transforme l'état du calculateur en BROUILLON de fiche de production :
//   - fige la formule (snapshot JSON : paramètres + lignes + résultat calculé) ;
//   - pré-remplit les lignes « matières premières » avec leur lettre de phase
//     (A=aqueux, B=gras, C=trace) et la quantité théorique (modifiable à la pesée) ;
//   - propose des valeurs d'en-tête (procédé, forme, mode du n° de lot) déduites
//     du calcul — toutes restent modifiables dans l'écran fiche.
//
// Module PUR (aucun React) pour rester testable.

import type { ResultatSaponification } from '@shared/calculs/saponification'
import type { ResultatINCI } from '@shared/calculs/inci'
import type { LigneFicheInput, ProcedeSavon, FormeSavon } from '@shared/schemas/fiche'
import type { ModeLot } from '@shared/calculs/numeroLot'
import type { FicheDetail } from '@/lib/fiches'
import type { LigneHuile, LigneAjout, LigneSurgras, ParametresFormule } from './types'
import type { AllergeneSenteur } from './versEtiquetage'

export interface BrouillonFicheInput {
  params: ParametresFormule
  huiles: LigneHuile[]
  ajouts: LigneAjout[]
  surgras: LigneSurgras[]
  resultat: ResultatSaponification
  // Correspondance ref de corps gras -> INCI (issue du référentiel). Sert à
  // pré-remplir la colonne INCI des lignes de fiche. Absent/vide = saisie manuelle.
  inciParRef?: Record<string, string | null>
  // Pré-étiquetage (liste INCI + allergènes) déjà calculé par construireINCI() au
  // moment de l'enregistrement. FIGÉ tel quel dans le snapshot (immuable ensuite).
  etiquetage?: ResultatINCI
  // Avertissement figé avec la fiche : corps gras sans racine de sel (INCI provisoire).
  corpsGrasSansSelStem?: string[]
  // Allergènes certifiés par senteur (bruts { inci, pctDansParfum }), figés pour le pivot
  // « demande de DIP ». Absent/vide = aucune senteur allergène → rien n'est écrit dans le snapshot.
  allergenesSenteurs?: AllergeneSenteur[]
}

export interface BrouillonFiche {
  snapshotFormule: Record<string, unknown>
  poidsTheoriqueG: number
  procede: ProcedeSavon
  forme: FormeSavon
  modeLot: ModeLot
  lignes: LigneFicheInput[]
  // Nom de formule pré-rempli (utilisé à la DUPLICATION ; absent depuis le calculateur).
  nomFormule?: string
}

// Arrondi à 1 décimale (grammes).
const g = (n: number): number => Math.round(n * 10) / 10

// Le procédé moteur ('saf' = à froid, 'hp' = à chaud) pré-règle le procédé de la
// fiche et le MODE du n° de lot. Marseille / relargué ('RE') reste un choix manuel.
function procedeFiche(p: ParametresFormule['procede']): ProcedeSavon {
  return p === 'hp' ? 'a_chaud' : 'a_froid'
}
function modeLot(p: ParametresFormule['procede']): ModeLot {
  return p === 'hp' ? 'CH' : 'FR'
}

export function construireBrouillonFiche(input: BrouillonFicheInput): BrouillonFiche {
  const r = input.resultat
  const lignes: LigneFicheInput[] = []
  let ordre = 1
  const pousser = (phase: string, designation: string, qtePeseeG: number, inci?: string) => {
    lignes.push({ ordre: ordre++, phase, designation, qtePeseeG: g(qtePeseeG), inci, fait: false })
  }

  // INCI : déterministe et universel pour eau/alcalis ; depuis le référentiel
  // (ref -> inci) pour les corps gras et surgras. Vide = saisie manuelle.
  const inciDe = (ref: string): string | undefined => input.inciParRef?.[ref] || undefined

  // Phase A — aqueux : eau + alcalis (INCI normalisés).
  if (r.eauG > 0) pousser('A', 'Eau', r.eauG, 'Aqua')
  if (r.naohG > 0) pousser('A', 'Soude caustique (NaOH)', r.naohG, 'Sodium Hydroxide')
  if (r.kohG > 0) pousser('A', 'Potasse caustique (KOH)', r.kohG, 'Potassium Hydroxide')
  // Phase B — gras : huiles de la formule.
  for (const h of r.huilesDetail) pousser('B', h.nom, h.poidsG, inciDe(h.ref))
  // Phase C — trace : huiles de surgras nommées + ajouts.
  for (const s of r.surgrasDetail) pousser('C', s.nom, s.poidsG, inciDe(s.ref))
  for (const a of r.ajouts) pousser('C', a.nom, a.poidsG, inciDe(a.ref))

  return {
    snapshotFormule: {
      params: input.params,
      huiles: input.huiles,
      ajouts: input.ajouts,
      surgras: input.surgras,
      resultat: r,
      // Pré-étiquetage figé (liste INCI + allergènes) ; null si non calculable.
      etiquetage: input.etiquetage
        ? { ...input.etiquetage, corpsGrasSansSelStem: input.corpsGrasSansSelStem ?? [] }
        : null,
      // Allergènes certifiés par senteur, figés pour le pivot « demande de DIP ». Écrit
      // uniquement s'il y en a (formule non parfumée → clé absente, snapshot inchangé).
      ...(input.allergenesSenteurs && input.allergenesSenteurs.length > 0
        ? { allergenesSenteurs: input.allergenesSenteurs }
        : {}),
    },
    poidsTheoriqueG: g(r.masseTotaleLotG),
    procede: procedeFiche(input.params.procede),
    forme: input.params.formeSavon,
    modeLot: modeLot(input.params.procede),
    lignes,
  }
}

// Listes de valeurs valides pour sécuriser le mapping depuis une fiche persistée (où procede /
// forme sont des string nullables).
const FORMES_VALIDES: readonly FormeSavon[] = ['solide', 'liquide', 'pate', 'gel', 'poudre']

// Construit un BROUILLON de fiche à partir d'une fiche EXISTANTE, pour la DUPLIQUER (relancer
// une production identique). On RECOPIE : la formule figée (snapshot, poids théorique), l'en-tête
// (procédé, forme, nom de formule) et les lignes « matières premières » (désignation, phase,
// INCI, fournisseur, article lié au stock, quantité théorique). On NE recopie PAS : le numéro de
// lot (unique par compte, à ressaisir), le statut (la nouvelle fiche démarre 'en_cours'), les
// blocs process et observations (propres au lot), ni le lot fournisseur / la DDM (propres au lot
// d'origine). Module PUR (testable).
export function brouillonDepuisFiche(fiche: FicheDetail): BrouillonFiche {
  const procede: ProcedeSavon =
    fiche.procede === 'a_chaud' ? 'a_chaud' : fiche.procede === 'marseille' ? 'marseille' : 'a_froid'
  const modeLot: ModeLot = procede === 'a_chaud' ? 'CH' : procede === 'marseille' ? 'RE' : 'FR'
  const forme: FormeSavon = FORMES_VALIDES.includes(fiche.forme as FormeSavon)
    ? (fiche.forme as FormeSavon)
    : 'solide'
  return {
    snapshotFormule: fiche.snapshotFormule,
    poidsTheoriqueG: fiche.poidsTheoriqueG ?? 0,
    procede,
    forme,
    modeLot,
    nomFormule: fiche.nomFormule ?? undefined,
    lignes: fiche.lignes.map((l, i) => ({
      ordre: l.ordre ?? i + 1,
      phase: l.phase ?? undefined,
      designation: l.designation,
      inci: l.inci ?? undefined,
      fournisseur: l.fournisseur ?? undefined,
      articleId: l.articleId ?? undefined,
      qtePeseeG: l.qtePeseeG ?? undefined,
      fait: false,
    })),
  }
}
